import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { FiCalendar } from "react-icons/fi";
import { Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import moment from "moment";
import useMobileCheck from "../../../../Hooks/useMobileCheck";

function DatePicker({ label = "", minDate, maxDate, backDateSelection, disabledDates, date, setDate = () => {}, className = "", btnClass = "", error, onFocus = () => {} }) {
	const [ShowPicker, setShowPicker] = useState(false);
	const { isMobile } = useMobileCheck();
	const parsedMinDate = useMemo(() => {
		if (minDate) return moment(minDate).toDate();
		return !backDateSelection ? new Date() : undefined;
	}, [minDate, backDateSelection]);
	const parsedMaxDate = useMemo(() => {
		return maxDate ? moment(maxDate).toDate() : moment().add(1, "year").toDate();
	}, [maxDate]);
	const resolvedDate = useMemo(() => {
		// When no date is selected (e.g. DOB starts as null), open calendar
		// on an allowed date instead of "today", so users can pick quickly.
		if (date) return moment(date).toDate();
		if (backDateSelection && parsedMaxDate) return parsedMaxDate;
		if (parsedMinDate) return parsedMinDate;
		return new Date();
	}, [date, backDateSelection, parsedMinDate, parsedMaxDate]);

	//Popper stuff
	const [PopRef, setPopRef] = useState(null);
	const [PopElem, setPopElem] = useState(null);
	const { styles, attributes, update } = usePopper(PopRef, PopElem, {
		placement: isMobile ? "bottom" : "bottom-start",
		strategy: isMobile ? "fixed" : "absolute",
		modifiers: [
			{ name: "offset", options: { offset: [0, 8] } },
			{ name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
			{ name: "flip", options: { fallbackPlacements: ["top", "bottom"] } },
		],
	});

	// Close on outside click / touch and Esc
	useEffect(() => {
		if (!ShowPicker) return;
		const onPointer = (e) => {
			const target = e.target;
			if (!target) return;
			if (PopRef && PopRef.contains(target)) return;
			if (PopElem && PopElem.contains(target)) return;
			setShowPicker(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setShowPicker(false);
		};
		document.addEventListener("mousedown", onPointer, true);
		document.addEventListener("touchstart", onPointer, true);
		document.addEventListener("keydown", onKey, true);
		return () => {
			document.removeEventListener("mousedown", onPointer, true);
			document.removeEventListener("touchstart", onPointer, true);
			document.removeEventListener("keydown", onKey, true);
		};
	}, [ShowPicker, PopRef, PopElem]);

	// Fix first-open misalignment by forcing Popper recalculation after mount
	useEffect(() => {
		if (!ShowPicker) return;
		if (!update) return;
		let raf1 = requestAnimationFrame(() => {
			update();
			requestAnimationFrame(() => update());
		});
		return () => cancelAnimationFrame(raf1);
	}, [ShowPicker, update]);

	return (
		<div className={`relative ${className}`}>
			{label && <label className="block mb-2">{label}</label>}
			<div ref={setPopRef} className={`relative text-sm ring-1 ring-inset ${error ? "ring-red-500" : "ring-muted"} bg-canvas shadow-sm rounded-md p-2.5 align-center cursor-pointer ${btnClass}`} onClick={() => { onFocus(); setShowPicker(!ShowPicker); }}>
				{error && (
					<div className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2">
						<div className="relative whitespace-nowrap rounded bg-black px-3 py-1.5 text-xs font-medium text-white shadow-lg">
							{error}
							<span className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent border-t-black" />
						</div>
					</div>
				)}
				<FiCalendar className="mr-2" />
				<div className="whitespace-nowrap">{date ? moment(date).format("DD MMM YY") : "Select Date"}</div>
			</div>
			<Transition
				ref={setPopElem}
				style={styles.popper}
				{...attributes.popper}
				className="z-[70]"
				show={ShowPicker ? true : false}
			>
				<Calendar
					className="overflow-hidden border rounded-lg shadow-lg max-w-[95vw]"
					onChange={(item) => {
						setDate(moment(item).valueOf());
						setShowPicker(false);
					}}
					date={resolvedDate}
					shownDate={resolvedDate}
					minDate={parsedMinDate}
					maxDate={parsedMaxDate}
					disabledDates={disabledDates}
					color="#be3735"
					rangeColors={["#be3735"]}
				/>
			</Transition>
		</div>
	);
}

export default DatePicker;
